import { useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';

// Spoken-word VO is ~150 wpm; conversational ~120. We use a single mid-point
// for simplicity and label it as an estimate.
const WORDS_PER_MINUTE = 150;

interface Props {
  editor: Editor | null;
}

export default function WordCount({ editor }: Props) {
  const [stats, setStats] = useState<{ words: number; secs: number }>({ words: 0, secs: 0 });

  useEffect(() => {
    if (!editor) return;
    function compute() {
      const text = editor!.state.doc.textContent;
      const words = text.trim().length === 0 ? 0 : text.trim().split(/\s+/).length;
      const secs = Math.round((words / WORDS_PER_MINUTE) * 60);
      setStats({ words, secs });
    }
    compute();
    editor.on('update', compute);
    return () => {
      editor.off('update', compute);
    };
  }, [editor]);

  return (
    <span
      className="hidden lg:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-muted"
      title={`Estimated read time at ~${WORDS_PER_MINUTE} wpm`}
    >
      <span>{stats.words.toLocaleString()} words</span>
      <span className="text-faint" aria-hidden>·</span>
      <span>~{formatRuntime(stats.secs)}</span>
    </span>
  );
}

function formatRuntime(seconds: number): string {
  if (seconds < 1) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
